import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Heart, ShoppingBag, Minus, Plus, Truck, RotateCcw, Ruler, Check, Star, MessageSquare, Bell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { MEDIA, formatAUD } from "@/lib/media";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import RecentlyViewed from "@/components/RecentlyViewed";
import NotifyStock from "@/components/NotifyStock";
import SaleCountdown from "@/components/SaleCountdown";
import PeekInside from "@/components/PeekInside";
import ShareBar from "@/components/ShareBar";
import ShareReward from "@/components/ShareReward";
import SizeRecommender from "@/components/SizeRecommender";
import BookTeeUpsell from "@/components/BookTeeUpsell";
import CutoffNotice from "@/components/CutoffNotice";
import DeliveryEstimate from "@/components/DeliveryEstimate";
import SneakPeekRemindDialog, { requestSneakPeekReminder, reminderToast } from "@/components/SneakPeekRemind";
import { trackViewContent } from "@/lib/pixel";
import { ReviewPhotoPicker, ReviewPhotos } from "@/components/ReviewPhotos";
import { addRecent } from "@/lib/recentlyViewed";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWishlisted, setCartOpen } = useStore();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [personalisation, setPersonalisation] = useState("");
  const [remindOpen, setRemindOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const pickColour = (c) => { setColour(c); const img = product?.colour_images?.[c]; if (img) setActiveImage(img); };
  const [colour, setColour] = useState(null);
  const [qty, setQty] = useState(1);
  const [shareReward, setShareReward] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, average: null });
  const [canReview, setCanReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ author_name: "", email: "", phone: "", rating: 5, title: "", body: "", photos: [] });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rewardCode, setRewardCode] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useSeo(product?.seo_title || product?.name, product?.seo_description);

  const loadReviews = useCallback(() => api.get(`/products/${slug}/reviews`).then(({ data }) => setReviews(data)).catch(() => {}), [slug]);

  useEffect(() => {
    setNotFound(false); setSize(null); setColour(null); setQty(1); setActiveImage(null);
    api.get(`/products/${slug}`).then(({ data }) => { setProduct(data); addRecent(data); trackViewContent(data); }).catch(() => setNotFound(true));
    api.get(`/products/${slug}/related`).then(({ data }) => setRelated(data.products)).catch(() => {});
    loadReviews();
  }, [slug, loadReviews]);

  useEffect(() => {
    if (!user || !product) { setCanReview(false); return; }
    api.get("/account/reviewable").then(({ data }) =>
      setCanReview(data.products.some((p) => p.product_id === product.id))).catch(() => setCanReview(false));
  }, [user, product, reviews]);

  useEffect(() => {
    if (user) setReviewForm((f) => ({ ...f, author_name: f.author_name || (user.name || "").split(" ")[0], email: f.email || user.email || "" }));
  }, [user]);

  // "?review=1&email=…" from the review-reminder email: open the form pre-filled and scroll to it
  useEffect(() => {
    if (searchParams.get("review") !== "1" || !product) return;
    const email = searchParams.get("email") || "";
    setReviewForm((f) => ({ ...f, email: email || f.email }));
    setShowReviewForm(true);
    setTimeout(() => document.getElementById("reviews-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [searchParams, product]);

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      const { data } = await api.post(`/products/${slug}/reviews`, reviewForm);
      if (data.reward_code) {
        setRewardCode(data.reward_code);
        toast.success(`Thanks for the pup pic! Your 10% code ${data.reward_code} is on its way to your inbox 📸`, { duration: 8000 });
      } else {
        toast.success("Thanks for your review! It's live as a verified buyer review 🐾");
      }
      setReviewForm((f) => ({ ...f, rating: 5, title: "", body: "", photos: [] }));
      setShowReviewForm(false);
      loadReviews();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmittingReview(false); }
  };

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display font-extrabold text-3xl text-[#19324A]">We couldn't sniff out that product.</h1>
      <Link to="/shop" className="btn-primary mt-6 inline-flex">Back to Shop</Link>
    </div>
  );
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24"><div className="h-96 rounded-3xl bg-white/60 animate-pulse" /></div>;

  const soldOut = (product.stock ?? 0) <= 0;
  const onSale = !!product.sale_price;
  const pv = !onSale && (product.printify_variants || []).find((v) => (!size || v.size === size) && (!colour || v.colour === colour) && v.price);
  const price = pv ? pv.price : onSale ? product.sale_price : product.price;
  const needSize = (product.sizes?.length || 0) > 0;
  const needColour = (product.colours?.length || 0) > 0;
  const image = activeImage || (product.images || [])[0];

  const needPers = !!product.personalisation_label;
  const persMax = product.personalisation_max || 15;
  const flatShip = !product.printify && product.shipping_flat ? Number(product.shipping_flat) : 0;
  const validate = () => {
    const missing = (needColour && !colour) ? "colour" : (needSize && !size) ? "size" : (needPers && !personalisation.trim()) ? "pers" : null;
    if (!missing) return true;
    const target = document.getElementById(missing === "size" ? "pdp-size" : missing === "colour" ? "pdp-colour" : "pdp-pers") || document.getElementById("pdp-options");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (missing === "size") toast.error("Please choose a size.");
    else if (missing === "colour") toast.error("Please choose a colour.");
    else toast.error(`Please enter the ${product.personalisation_label.toLowerCase()}.`);
    return false;
  };
  const cartOpts = () => ({ size, colour, quantity: qty, personalisation: needPers ? personalisation.trim() : null });
  const handleAdd = () => {
    if (soldOut || !validate()) return;
    addToCart(product, cartOpts());
    toast.success(`${product.name} added to your bag 🐾`);
  };
  const handleBuyNow = () => {
    if (soldOut || !validate()) return;
    addToCart(product, cartOpts());
    setCartOpen(false);
    navigate("/checkout");
  };

  const relatedTop = related.slice(0, 4);
  const complete = related.filter((r) => r.category !== product.category).slice(0, 4);
  const wished = isWishlisted(product.id);
  const remindMe = () => {
    if (user) { requestSneakPeekReminder({ email: user.email, product }).then(() => reminderToast(product)).catch((err) => toast.error(formatApiError(err.response?.data?.detail))); }
    else setRemindOpen(true);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-sm text-[#19324A]/50 mb-6"><Link to="/" className="hover:text-[#FF6B9D]">Home</Link> / <Link to="/shop" className="hover:text-[#FF6B9D]">Shop</Link> / <span className="text-[#19324A]/80">{product.name}</span></div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="relative rounded-3xl overflow-hidden soft-shadow bg-white group">
              <img key={image} src={image} alt={product.name} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700 animate-in fade-in duration-300" data-testid="product-main-image" />
              <div className="absolute top-4 left-4 flex flex-col gap-1.5">
                {product.is_new && <span className="px-3 py-1 rounded-full text-xs font-display font-bold bg-[#7ED957]">NEW</span>}
                {onSale && <span className="px-3 py-1 rounded-full text-xs font-display font-bold bg-[#FF6B9D] text-white">SALE</span>}
              </div>
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-1" data-testid="product-thumbnails">
                {product.images.map((img, i) => (
                  <button key={img} type="button" onClick={() => setActiveImage(img)} aria-label={`View image ${i + 1}`} data-testid={`product-thumb-${i}`}
                    className={`shrink-0 rounded-xl ring-2 soft-shadow transition-transform hover:scale-105 ${image === img ? "ring-[#FFD93D]" : "ring-white"}`}>
                    <img src={img} alt="" className="w-20 h-20 rounded-xl object-cover" />
                  </button>
                ))}
              </div>
            )}
            <PeekInside pages={product.peek_pages || []} title={product.name} />
            <BookTeeUpsell productId={product.id} />
          </div>

          {/* Info */}
          <div>
            {product.is_placeholder && <span className="inline-block mb-3 text-xs font-semibold text-[#A78BFA] bg-[#A78BFA]/10 rounded-full px-3 py-1">Sample product — replace in admin</span>}
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#19324A] leading-tight">{product.name}</h1>
            {product.off_season && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="inline-flex items-center gap-2 rounded-full bg-[#A78BFA]/15 text-[#5B3FBF] text-xs font-bold px-3 py-1.5" data-testid="pdp-off-season">
                  Off-season — back in the shop{product.season_returns ? ` from ${new Date(product.season_returns).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : " soon"}. You can still order it today.
                </p>
                {product.seasonal && (
                  <button onClick={remindMe} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#FF6B9D] text-[#FF6B9D] text-xs font-bold px-3 py-1 hover:bg-[#FF6B9D] hover:text-white transition-colors" data-testid="pdp-remind-btn">
                    <Bell className="w-3.5 h-3.5" /> Remind me when it opens
                  </button>
                )}
              </div>
            )}
            {product.off_season && product.seasonal && <SneakPeekRemindDialog open={remindOpen} onOpenChange={setRemindOpen} product={product} />}
            <div className="mt-4 flex items-center gap-3">
              <span className="font-display font-extrabold text-3xl text-[#19324A]">{formatAUD(price)}</span>
              {onSale && <span className="text-lg text-[#19324A]/40 line-through">{formatAUD(product.price)}</span>}
              <span className="text-sm text-[#19324A]/50">AUD</span>
            </div>
            {flatShip > 0 && <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-[#19324A]/70" data-testid="pdp-flat-postage"><Truck className="w-4 h-4 text-[#4CC9F0]" /> + {formatAUD(flatShip)} flat postage at checkout <span className="text-[#19324A]/45">(once per order)</span></p>}
            {product.local_pickup && <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#166534] bg-[#7ED957]/15 rounded-full px-3 py-1" data-testid="pdp-local-pickup"><Check className="w-4 h-4" /> Local pickup available — Slacks Creek QLD (no postage, choose at checkout)</p>}
            {onSale && product.sale_ends_at && <div className="mt-2"><SaleCountdown ends={product.sale_ends_at} testid="pdp-sale-countdown" /></div>}
            <p className="mt-2 text-sm font-semibold" style={{ color: soldOut ? "#FF6B9D" : "#5aa93e" }}>
            {soldOut ? "Currently sold out" : product.printify ? "Made to order — printed & shipped in 2–5 business days" : "In stock — ready to ship"}
            </p>

            <p className="mt-5 text-[#19324A]/80 leading-relaxed whitespace-pre-line">{product.description.split("\n\n")[0]}</p>
            <div id="pdp-options" />

            {needColour && (
              <div className="mt-6 scroll-mt-28" id="pdp-colour">
                <p className="font-display font-semibold text-[#19324A] mb-2">Colour: <span className="text-[#19324A]/60">{colour || "Choose one"}</span></p>
                <div className="flex flex-wrap gap-2">
                  {product.colours.map((c) => (
                    <button key={c} onClick={() => pickColour(c)} data-testid={`colour-${c}`}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${colour === c ? "border-[#FF6B9D] bg-[#FF6B9D]/10 text-[#19324A]" : "border-[#19324A]/15 text-[#19324A]/70 hover:border-[#FF6B9D]"}`}>{c}</button>
                  ))}
                </div>
              </div>
            )}
            {needSize && (
              <div className="mt-6 scroll-mt-28" id="pdp-size">
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  <p className="font-display font-semibold text-[#19324A]">Size: <span className="text-[#19324A]/60">{size || "Choose one"}</span></p>
                  <div className="flex items-center gap-3">
                    <SizeRecommender productId={product.id} sizes={product.sizes} onPick={setSize} />
                    <Link to={`/size-guide?tab=${/\b(cap|hat|snapback)s?\b/i.test(`${product.name} ${product.category}`) ? "caps" : product.audience === "kids" ? "kids" : product.audience === "dog" ? "dogs" : "unisex"}`} className="text-sm text-[#4CC9F0] font-semibold inline-flex items-center gap-1" data-testid="pdp-size-guide-link"><Ruler className="w-4 h-4" />Size Guide</Link>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                      className={`min-w-[3rem] px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-colors ${size === s ? "border-[#FFD93D] bg-[#FFD93D]/30 text-[#19324A]" : "border-[#19324A]/15 text-[#19324A]/70 hover:border-[#FFD93D]"}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {needPers && (
              <div className="mt-5 rounded-2xl border-2 border-[#FFD93D] bg-[#FFF9EE] p-4 scroll-mt-28" id="pdp-pers" data-testid="pdp-personalisation">
                <label htmlFor="pdp-personalisation-input" className="block font-display font-bold text-[#19324A]">{product.personalisation_label} <span className="text-[#FF6B9D]">*</span></label>
                <input id="pdp-personalisation-input" data-testid="pdp-personalisation-input" value={personalisation} maxLength={persMax}
                  onChange={(e) => setPersonalisation(e.target.value)} placeholder="e.g. Quinn"
                  className="mt-2 w-full rounded-xl border-2 border-[#19324A]/10 bg-white px-4 py-3 text-base font-display font-semibold tracking-wide outline-none focus:border-[#FF6B9D]" />
                <div className="mt-1.5 flex justify-between text-xs text-[#19324A]/55">
                  <span>{product.personalisation_note || "Please double-check spelling — personalised items can't be returned."}</span>
                  <span data-testid="pdp-personalisation-count">{personalisation.length}/{persMax}</span>
                </div>
              </div>
            )}
            {needPers && <CutoffNotice />}
            <DeliveryEstimate slug={product.slug} />
            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white rounded-full soft-shadow px-2">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#FFF9EE]" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-display font-bold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#FFF9EE]" aria-label="Increase"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={() => toggleWishlist(product.id, product.sale_price || product.price, product)} data-testid="pdp-wishlist"
                className="w-12 h-12 rounded-full bg-white soft-shadow flex items-center justify-center hover:scale-105 transition-transform">
                <Heart className={`w-5 h-5 ${wished ? "fill-[#FF6B9D] text-[#FF6B9D]" : "text-[#19324A]/60"}`} />
              </button>
              <ShareBar compact title={product.name} path={`/api/share/product/${product.slug}`} testid="pdp-share" onFacebook={() => setTimeout(() => setShareReward(true), 800)} />
            </div>
            {shareReward && <ShareReward productId={product.id} onClose={() => setShareReward(false)} />}

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button onClick={handleAdd} disabled={soldOut} className="btn-primary flex-1 disabled:opacity-50" data-testid="pdp-add-to-cart">
                <ShoppingBag className="w-5 h-5" />{soldOut ? "Sold Out" : "Add to Bag"}
              </button>
              <button onClick={handleBuyNow} disabled={soldOut} className="btn-pink flex-1 disabled:opacity-50" data-testid="pdp-buy-now">Buy Now</button>
            </div>

            {soldOut && <NotifyStock productId={product.id} />}

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-[#19324A]/70" data-testid="pdp-shipping-note"><Truck className="w-5 h-5 text-[#4CC9F0]" /> {flatShip ? <span>Flat <strong className="text-[#19324A]">{formatAUD(flatShip)}</strong> postage, Australia-wide</span> : "Shipping calculated at checkout"}</div>
              <div className="flex items-center gap-2 text-[#19324A]/70"><RotateCcw className="w-5 h-5 text-[#7ED957]" /> Easy returns — see policy</div>
            </div>

            <Accordion type="single" collapsible className="mt-8" defaultValue="desc">
              <AccordionItem value="desc"><AccordionTrigger className="font-display font-semibold text-[#19324A]">Description</AccordionTrigger><AccordionContent className="text-[#19324A]/75 whitespace-pre-line">{product.description}</AccordionContent></AccordionItem>
              {product.materials && <AccordionItem value="mat"><AccordionTrigger className="font-display font-semibold text-[#19324A]">Materials</AccordionTrigger><AccordionContent className="text-[#19324A]/75">{product.materials}</AccordionContent></AccordionItem>}
              {product.care && <AccordionItem value="care"><AccordionTrigger className="font-display font-semibold text-[#19324A]">Care Instructions</AccordionTrigger><AccordionContent className="text-[#19324A]/75">{product.care}</AccordionContent></AccordionItem>}
              {product.sizing && <AccordionItem value="size"><AccordionTrigger className="font-display font-semibold text-[#19324A]">Sizing</AccordionTrigger><AccordionContent className="text-[#19324A]/75">{product.sizing}</AccordionContent></AccordionItem>}
              <AccordionItem value="ship"><AccordionTrigger className="font-display font-semibold text-[#19324A]">Shipping</AccordionTrigger><AccordionContent className="text-[#19324A]/75" data-testid="pdp-shipping-accordion">
                {flatShip ? <>This item ships directly from The Happy Bulldog Co. for a flat <strong>{formatAUD(flatShip)}</strong> postage fee, charged once per order no matter how many you add (it's shown as its own line at checkout). </> : "Shipping options and costs are calculated at checkout. "}
                Full shipping timeframes will be confirmed by The Happy Bulldog Co. — see our <Link to="/legal/shipping-policy" className="text-[#4CC9F0] underline">Shipping Policy</Link>.</AccordionContent></AccordionItem>
              <AccordionItem value="ret"><AccordionTrigger className="font-display font-semibold text-[#19324A]">Returns</AccordionTrigger><AccordionContent className="text-[#19324A]/75">See our <Link to="/legal/refund-policy" className="text-[#4CC9F0] underline">Refund Policy</Link> for full details.</AccordionContent></AccordionItem>
            </Accordion>
          </div>
        </div>

        {relatedTop.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#19324A] mb-6">You May Also Love 🐾</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">{relatedTop.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          </section>
        )}
        {complete.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#19324A] mb-6">Complete the Adventure</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">{complete.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          </section>
        )}

        <RecentlyViewed excludeId={product?.id} className="mt-16 !px-0" />

        {/* REVIEWS — only verified buyers, only shown when they exist */}
        <section className="mt-16" id="reviews-section" data-testid="reviews-section">
          {rewardCode && (
            <div className="mb-4 rounded-2xl bg-[#FF6B9D]/10 border-2 border-[#FF6B9D] p-4 max-w-xl" data-testid="review-reward-banner">
              <p className="text-sm text-[#19324A]">📸 Thanks for the pup pic! Your thank-you code: <strong className="font-display text-base tracking-wide" data-testid="review-reward-code">{rewardCode}</strong> — 10% off your next order (also emailed to you).</p>
            </div>
          )}
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#19324A] mb-6">Reviews</h2>
          {reviews.count > 0 ? (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-6 h-6 ${n <= Math.round(reviews.average) ? "fill-[#FFD93D] text-[#FFD93D]" : "text-[#19324A]/20"}`} />)}</div>
                <span className="font-display font-bold text-lg text-[#19324A]">{reviews.average}</span>
                <span className="text-[#19324A]/55">· {reviews.count} {reviews.count === 1 ? "review" : "reviews"}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {reviews.reviews.map((r) => (
                  <div key={r.id} className="bg-white rounded-2xl p-5 soft-shadow" data-testid="review-item">
                    <div className="flex items-center justify-between">
                      <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#FFD93D] text-[#FFD93D]" : "text-[#19324A]/20"}`} />)}</div>
                      {r.verified !== false
                        ? <span className="text-xs font-bold text-[#7ED957] bg-[#7ED957]/15 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid={`review-badge-${r.id}`}><Check className="w-3 h-3" />Verified buyer</span>
                        : <span className="text-xs font-bold text-[#4CC9F0] bg-[#4CC9F0]/15 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid={`review-badge-${r.id}`}><MessageSquare className="w-3 h-3" />Reader review</span>}
                    </div>
                    {r.title && <p className="font-display font-bold text-[#19324A] mt-2">{r.title}</p>}
                    <p className="text-[#19324A]/75 text-sm mt-1 whitespace-pre-line">{r.body}</p>
                    <ReviewPhotos photos={r.photos} testid={`review-photos-${r.id}`} />
                    <p className="text-xs text-[#19324A]/45 mt-2">— {r.author_name}{r.age_group && <span data-testid={`review-age-${r.id}`}> · {r.age_group}</span>}</p>
                    {r.reply?.body && (
                      <div className="mt-3 rounded-xl bg-[#FFF9EE] border-l-4 border-[#FFD93D] p-3" data-testid={`review-reply-${r.id}`}>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-[#19324A]/55">🐾 Reply from {r.reply.by || "The Happy Bulldog Co."}</p>
                        <p className="text-sm text-[#19324A]/80 mt-1 whitespace-pre-line">{r.reply.body}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[#19324A]/60 bg-white rounded-2xl p-6 soft-shadow flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#4CC9F0]" />No reviews yet — be the first verified buyer to share the love.</p>
          )}

          {(canReview || showReviewForm) ? (
            <form onSubmit={submitReview} className="mt-6 bg-white rounded-2xl p-6 soft-shadow max-w-xl" data-testid="review-form">
              <h3 className="font-display font-bold text-lg text-[#19324A] mb-1">Write a review</h3>
              <p className="text-xs text-[#19324A]/55 mb-4" data-testid="review-form-note">Reviews are published only for verified purchases — enter the email and phone number you used at checkout and we'll match them to your order.</p>
              <div className="grid sm:grid-cols-3 gap-3 mb-3">
                <input required maxLength={80} value={reviewForm.author_name} onChange={(e) => setReviewForm((f) => ({ ...f, author_name: e.target.value }))} placeholder="Your name" className="w-full rounded-xl border-2 border-[#19324A]/10 px-4 py-2.5 outline-none focus:border-[#FFD93D]" data-testid="review-name" />
                <input required type="email" value={reviewForm.email} onChange={(e) => setReviewForm((f) => ({ ...f, email: e.target.value }))} placeholder="Checkout email" className="w-full rounded-xl border-2 border-[#19324A]/10 px-4 py-2.5 outline-none focus:border-[#FFD93D]" data-testid="review-email" />
                <input required type="tel" value={reviewForm.phone} onChange={(e) => setReviewForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Checkout phone" className="w-full rounded-xl border-2 border-[#19324A]/10 px-4 py-2.5 outline-none focus:border-[#FFD93D]" data-testid="review-phone" />
              </div>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setReviewForm((f) => ({ ...f, rating: n }))} aria-label={`${n} stars`} data-testid={`review-star-${n}`}>
                    <Star className={`w-8 h-8 ${n <= reviewForm.rating ? "fill-[#FFD93D] text-[#FFD93D]" : "text-[#19324A]/20"}`} />
                  </button>
                ))}
              </div>
              <input value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (optional)" className="w-full rounded-xl border-2 border-[#19324A]/10 px-4 py-2.5 outline-none focus:border-[#FFD93D] mb-3" data-testid="review-title" />
              <textarea required rows={3} value={reviewForm.body} onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))} placeholder="Tell the pack what you thought…" className="w-full rounded-xl border-2 border-[#19324A]/10 px-4 py-2.5 outline-none focus:border-[#FFD93D] resize-none mb-3" data-testid="review-body" />
              <ReviewPhotoPicker photos={reviewForm.photos} onChange={(photos) => setReviewForm((f) => ({ ...f, photos }))} />
              <p className="text-xs font-semibold text-[#FF6B9D] mt-2" data-testid="review-photo-reward-note">📸 Add a photo of your pup with it and we'll email you a 10% off code as a thank-you.</p>
              <button disabled={submittingReview} className="btn-pink mt-3" data-testid="review-submit">{submittingReview ? "Checking your order…" : "Post Review"}</button>
            </form>
          ) : (
            <div className="mt-4 bg-white rounded-2xl p-5 soft-shadow max-w-xl flex flex-wrap items-center justify-between gap-3" data-testid="review-gate">
              <p className="text-sm text-[#19324A]/65"><Check className="inline w-4 h-4 text-[#7ED957] mr-1" />Bought this? Only verified buyers can review — we match your checkout email and phone.</p>
              <button onClick={() => setShowReviewForm(true)} className="btn-outline !py-2 text-sm" data-testid="review-open-form">Write a review</button>
            </div>
          )}
        </section>
      </div>

      {/* Sticky mobile add-to-cart */}
      <div className="lg:hidden sticky bottom-0 z-30 bg-white border-t border-[#19324A]/10 p-3 flex items-center gap-3 shadow-[0_-4px_16px_rgba(25,50,74,0.08)]" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="min-w-0">
          <div className="font-display font-bold text-lg text-[#19324A] leading-tight">{formatAUD(price)}</div>
          {(needSize || needColour) && <div className="text-[11px] text-[#19324A]/55 truncate" data-testid="sticky-selection">{[colour, size].filter(Boolean).join(" · ") || "Choose options"}</div>}
        </div>
        <button onClick={handleAdd} disabled={soldOut} className="btn-primary flex-1 !py-3 disabled:opacity-50" data-testid="sticky-add-to-cart"><ShoppingBag className="w-5 h-5" />{soldOut ? "Sold Out" : "Add to Bag"}</button>
      </div>
    </div>
  );
}
